/**
 * Typed API error for service layer responses.
 * Thrown by service functions on non-OK HTTP responses.
 * Consumed by hooks, pages, and ResourceErrorPage for structured error handling.
 */

/**
 * Safely parse a JSON value that may be double-serialized by the control plane.
 * If the parsed result is a string that looks like JSON (starts with `{` or `[`),
 * it is parsed again. Plain strings are returned as-is. Returns `null` on failure.
 */
export function parseJsonBody(text: string): unknown {
  try {
    let parsed = JSON.parse(text);
    if (typeof parsed === 'string') {
      const trimmed = parsed.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        parsed = JSON.parse(parsed);
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

interface ParsedErrorBody {
  error?: { code?: string; message?: string };
  message?: string;
  code?: string;
}

/**
 * Extract a user-facing error message from a response body string.
 * Handles raw text, `{ error: { message } }` JSON, `{ message }` JSON, and
 * bodies that arrive double-encoded as a JSON string. Returns `null` when no
 * recognizable message is found so callers can choose their own fallback.
 */
export function extractErrorMessage(text: string): string | null {
  if (!text) return null;
  const body = parseJsonBody(text);
  if (typeof body === 'string') return body || null;
  if (body && typeof body === 'object') {
    const obj = body as ParsedErrorBody;
    return obj.error?.message ?? obj.message ?? null;
  }
  return null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * Create ApiError from a fetch Response.
   * Attempts to parse JSON body for error details.
   */
  static async fromResponse(response: Response): Promise<ApiError> {
    let message = response.statusText || `HTTP ${response.status}`;
    let code: string | undefined;

    try {
      const text = await response.text();
      const body = parseJsonBody(text);
      if (typeof body === 'string') {
        return new ApiError(body || message, response.status);
      }
      if (body && typeof body === 'object') {
        const obj = body as ParsedErrorBody;
        if (obj.error?.message) {
          message = obj.error.message;
          code = obj.error.code;
        } else if (obj.message) {
          message = obj.message;
          code = obj.code;
        }
      }
    } catch {
      // Body not JSON or empty — use status text
    }

    return new ApiError(message, response.status, code);
  }
}
